/**
 * Derives the brain panel's resting-state banner from real published
 * events — never from `life.assessment.sleep_state` alone (owner
 * requirement 2026-09-26: "`life.assessment.sleep_state` may read 'awake'
 * during a closed session after a restart (known gap, S29) — derive the
 * displayed state from the calendar transition + denial reason, not from
 * sleep_state alone").
 *
 * Two real, verified event shapes feed this (live host, 2026-09-26):
 * - `calendar.session_transition`: `{at, from_state, market_id, to_state}`
 *   — `to_state` is one of `closed`/`overnight`/`premarket`/`after_hours`/
 *   `regular` (verified: a real transition to `"closed"` exists on the
 *   host).
 * - `runtime.compute_request_rejected`: `{observation_cursor, organism_id,
 *   reason}` — `reason` is one of `insufficient_samples`/
 *   `prior_session_samples`/`market_warmup` (verified:
 *   `"insufficient_samples"` exists on the host).
 *
 * Never fabricates a specific "next open" timestamp — this frontend has no
 * calendar dataset to compute one honestly, so the message names the
 * *state*, not a guessed time.
 */

import type { LogEvent } from "../types/events";

export interface SessionStatus {
  readonly show: boolean;
  readonly message: string;
}

/** Exported so `src/panels/summaryStrip.ts` reads the exact same labels
 * instead of maintaining its own copy — a code-review finding (round 2 of
 * the observatory reshape's self-review) caught that a duplicated copy had
 * already drifted on the "has a wake" semantics (see `latestByKind`'s doc
 * comment below), so the fix is sharing the one true source, not just the
 * label text. */
export const STATE_LABELS: Readonly<Record<string, string>> = {
  closed: "closed",
  overnight: "overnight",
  premarket: "pre-market",
  after_hours: "after-hours",
  regular: "regular session",
};

/** Exported (R3, "observatory visibility of smoother state") so
 * `src/panels/sizingView.ts`'s `decisionText` reads the exact same S28
 * warm-up wording for the two reason codes the two event vocabularies share
 * (`insufficient_samples`/`prior_session_samples`) instead of maintaining a
 * second, independently-drifting copy — the same "one true source" rule
 * `STATE_LABELS`'s own doc comment already states for `summaryStrip.ts`. */
export const REASON_LABELS: Readonly<Record<string, string>> = {
  insufficient_samples: "no samples yet from the current session",
  prior_session_samples: "carrying only prior-session samples",
  market_warmup: "market warm-up window",
};

/** Exported for `summaryStrip.ts` (see `STATE_LABELS`'s doc comment). Finds
 * the highest-`seq` event of a given `kind` — plain linear scan, no
 * assumption about input order. */
export function latestByKind(events: readonly LogEvent[], kind: string): LogEvent | null {
  let latest: LogEvent | null = null;
  for (const event of events) {
    if (event.kind !== kind) continue;
    if (!latest || event.seq > latest.seq) latest = event;
  }
  return latest;
}

/** The organism's CURRENT session signal, resolved from the published events (FEM).
 *
 * Two fixes for the 24/7 crypto (female) parent, shared by this module and
 * `summaryStrip.ts` so the two can never disagree:
 *
 * - **Session.** Her calendar never leaves `regular`, so she emits exactly ONE
 *   `calendar.session_transition` in her whole life (the process-start
 *   bootstrap; restarts restore it silently). The publisher keeps a bounded
 *   window of event chunks, so that one transition ages out within hours and the
 *   session used to read "unknown". Every `brain.wake` carries its own
 *   `session_state`, so the newest of (latest transition, latest wake) wins.
 * - **Rejection.** A `runtime.compute_request_rejected` only describes "now" if
 *   no wake has happened since it; previously ANY rejection anywhere in the
 *   window pinned the strip to "asleep" for thousands of later, successful
 *   wakes (both parents; the female is stuck longest, having no daily
 *   transitions).
 */
export interface CurrentSessionSignal {
  readonly transition: LogEvent | null;
  readonly rejection: LogEvent | null;
  readonly latestWake: LogEvent | null;
  readonly toState: string | null;
}

export function currentSessionSignal(events: readonly LogEvent[]): CurrentSessionSignal {
  const transition = latestByKind(events, "calendar.session_transition");
  const rawRejection = latestByKind(events, "runtime.compute_request_rejected");
  // Same filter as `wakeTimeline.ts`'s `buildWakeTimelineFromEvents`.
  const latestWake = latestByKind(
    events.filter((event) => Boolean(event.payload)),
    "brain.wake",
  );
  const transitionState = transition?.payload?.["to_state"];
  const wakeState = latestWake?.payload?.["session_state"];
  let toState: string | null = typeof transitionState === "string" ? transitionState : null;
  if (
    typeof wakeState === "string" &&
    latestWake &&
    (!transition || latestWake.seq > transition.seq)
  ) {
    toState = wakeState;
  }
  const rejection =
    rawRejection && (!latestWake || rawRejection.seq > latestWake.seq) ? rawRejection : null;
  return { transition, rejection, latestWake, toState };
}

export function deriveSessionStatus(events: readonly LogEvent[], hasWakes: boolean): SessionStatus {
  const { transition, rejection, toState } = currentSessionSignal(events);

  const toStateText = toState;
  const reason = rejection?.payload?.["reason"];
  const reasonText = typeof reason === "string" ? reason : null;

  if (!transition && !rejection && !toStateText) {
    // No session signal at all — either nothing has run, or this is a v1
    // fixture that never emits these kinds. Say nothing extra when wakes
    // already exist; otherwise a plain "no wake recorded yet" is enough
    // (rendered by the scrubber label itself, not this banner).
    return { show: false, message: "" };
  }
  if (hasWakes && toStateText === "regular" && !rejection) {
    return { show: false, message: "" };
  }

  const stateLabel = toStateText ? (STATE_LABELS[toStateText] ?? toStateText) : "unknown";
  const parts = [`Session: ${stateLabel}`];
  const reasonLabel = reasonText ? (REASON_LABELS[reasonText] ?? reasonText) : null;
  if (reasonLabel) parts.push(reasonLabel);
  parts.push(hasWakes ? "playback below shows the last recorded wake" : "no wake recorded yet");
  return { show: true, message: parts.join(" — ") };
}
